import { LitElement, type PropertyValues } from 'lit'
import { state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { ORDER_TEMPLATES_STORE, type OrderTemplatesStore } from 'components/discovery/shared/order-templates/stores/order-templates-store.js'
import { DEFAULT_ROLE_PERMISSIONS, RolePermissionsContext, type RolePermissions } from 'components/shared/role/role-provider.js'

type Constructor<T = object> = new (...args: any[]) => T

/**
 * Loads the shared order templates for a component: requests them on connect, derives the
 * endpoints once `csc-role-provider` reports a logged-in user, and folds the load into
 * `updateComplete`.
**/
export const OrderTemplatesLoadingMixin = <T extends Constructor<LitElement>>(superClass: T) => {
  class OrderTemplatesLoadingMixinClass extends superClass {
    @inject(ORDER_TEMPLATES_STORE)
    private orderTemplatesLoadingStore!: OrderTemplatesStore

    @consume({ context: RolePermissionsContext, subscribe: true })
    @state()
    private rolePermissions: RolePermissions = DEFAULT_ROLE_PERMISSIONS

    private orderTemplatesLoaded: Promise<void> = Promise.resolve()

    override connectedCallback() {
      super.connectedCallback()
      this.orderTemplatesLoaded = this.orderTemplatesLoadingStore.ensureOrderTemplates()
    }

    protected override willUpdate(changedProperties: PropertyValues) {
      super.willUpdate(changedProperties)
      if (changedProperties.has('rolePermissions') && this.rolePermissions.loggedIn) {
        this.orderTemplatesLoaded = this.loadWithDerivedEndpoints()
      }
    }

    override async getUpdateComplete(): Promise<boolean> {
      const result = await super.getUpdateComplete()
      await this.orderTemplatesLoaded
      return result
    }

    private async loadWithDerivedEndpoints() {
      await this.orderTemplatesLoadingStore.useDerivedEndpoints()
      if (!this.isConnected) return
      await this.orderTemplatesLoadingStore.ensureOrderTemplates()
    }
  }

  return OrderTemplatesLoadingMixinClass as T
}
